
"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { GlowingEffect } from "./GlowingEffect";
import { cn } from "../../lib/utils";

const testimonials = [
  {
    text: "We were losing 30% of our signal revenue to Telegram 'mirror' channels. Aure OS identified the leakers using their watermarking and we haven't seen a leak in 3 weeks. It's paid for itself 10x over.",
    name: "Jake M.",
    role: "Founder, Capital Flow",
  },
  {
    text: "Our $997 course was being sold for $50 on group-buy sites. Since switching to Aure, those links are dead within hours. The white-label portal also makes our brand look much more professional.",
    name: "Elena S.",
    role: "The Ecom System",
  },
  {
    text: "Integrating our Whop store with the Aure OS DRM was seamless. My members love the new 'Member View' and I love that my strategy scripts are actually protected now.",
    name: "Marcus T.",
    role: "Lead Dev, Script Lab",
  },
  {
    text: "Finally, a solution that actually cares about the creator. The leak analysis report we get every Friday is eye-opening. We've banned over 40 resellers since deployment.",
    name: "Sarah J.",
    role: "Wealth Circle Admin",
  },
  {
    text: "Digital scarcity is hard to maintain in 2024. Aure OS makes it possible. If you're selling high-ticket info on Whop, you're leaving money on the table without this.",
    name: "Tom R.",
    role: "Pips & Profits Mentor",
  },
  {
    text: "The setup was literally done for me by their team. I didn't have to touch a single line of code, and now my content is behind a digital fortress. 10/10.",
    name: "Chloe V.",
    role: "Design Academy Founder",
  },
  {
    text: "My conversion rate on Whop went up because people can't find 'free' versions of my PDF anymore. If they want the value, they have to pay the price. Best investment of the year.",
    name: "Hassan A.",
    role: "Market Liquidity Group",
  },
  {
    text: "The device-locking feature is a game changer. We noticed some users were sharing logins across 5 different IPs. Aure blocked them instantly. My profit margins have never been better.",
    name: "Sana S.",
    role: "Options Mastery Elite",
  },
  {
    text: "Stopping resellers isn't just about money, it's about the integrity of the community. Aure OS ensures only real students are in the mix. Total peace of mind for our coaching staff.",
    name: "Farhan S.",
    role: "Alpha Scalp Strategy",
  },
];

const TestimonialCard: React.FC<{ testimonial: typeof testimonials[0] }> = ({ testimonial }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative p-8 rounded-[32px] border border-border/40 bg-card/40 backdrop-blur-sm max-w-xs w-full transition-all duration-500 hover:scale-[1.02] group will-change-transform transform-gpu"
    >
      <GlowingEffect
        spread={40}
        glow={isHovered}
        disabled={false}
        proximity={80}
        inactiveZone={0.01}
        borderWidth={1.5}
      />
      <div className="relative z-10 text-sm font-light leading-relaxed text-muted-foreground italic">
        "{testimonial.text}"
      </div>
      <div className="relative z-10 flex items-center gap-3 mt-8">
        <div className="flex flex-col border-l border-primary/20 pl-4">
          <div className="text-xs font-bold tracking-[0.2em] leading-tight text-foreground uppercase">
            {testimonial.name}
          </div>
          <div className="text-[9px] leading-tight opacity-30 tracking-[0.3em] uppercase font-bold mt-1.5">
            {testimonial.role}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TestimonialsColumn = (props: {
  className?: string;
  testimonials: typeof testimonials;
  duration?: number;
}) => {
  return (
    <div className={cn(props.className, "transform-gpu")}>
      <motion.div
        animate={{
          translateY: "-50%",
        }}
        transition={{
          duration: props.duration || 10,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6 will-change-transform transform-gpu"
      >
        {[...new Array(2)].map((_, index) => (
          <React.Fragment key={index}>
            {props.testimonials.map((testimonial, i) => (
              <TestimonialCard key={`${index}-${i}`} testimonial={testimonial} />
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

const firstColumn = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn = testimonials.slice(6, 9);

export const Testimonials = () => {
  return (
    <section className="bg-background py-24 relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none transform-gpu">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-purple/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-magenta/5 blur-[120px] rounded-full" />
      </div>

      <div className="container z-10 mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="flex flex-col items-center justify-center max-w-[540px] mx-auto text-center"
        >
          {/* Reviews Pill */}
          <div className="flex justify-center mb-6">
            <div className="relative rounded-full transform-gpu group cursor-default">
              <GlowingEffect
                spread={25}
                glow={true}
                disabled={false}
                proximity={50}
                inactiveZone={0.01}
                borderWidth={1.5}
              />
              <div className="relative flex items-center justify-center rounded-full border border-border/50 px-6 py-2.5 backdrop-blur-md transition-all bg-background/50 text-muted-foreground group-hover:text-foreground">
                <span className="text-[10px] font-bold tracking-[0.3em] uppercase">
                  Proof of Performance
                </span>
              </div>
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-foreground">
            Trusted by Whop's <span className="italic">Elite</span>
          </h2>
          <p className="mt-4 text-muted-foreground font-light leading-relaxed">
            Real feedback from creators who secured their assets and recovered lost revenue.
          </p>
        </motion.div>

        <div className="flex justify-center gap-6 mt-16 [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)] max-h-[740px] overflow-hidden transform-gpu">
          <TestimonialsColumn testimonials={firstColumn} duration={25} />
          <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={35} />
          <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={30} />
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
